import type { DraftAdjustment, DraftForecast } from "~/components/demand-2/demandDraftState"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import type { RouterOutputs } from "~/utils/api"
import type { PlanPeriod } from "~/utils/demandPlanPeriod"
import type { NoBasisCause, PublishSkipReason } from "~/utils/demandPublishSkips"
import { snapDelta } from "~/utils/demandPublishState"
import type { BaselineSplit } from "~/utils/demandReconstruct"

export type EffectiveDemand = {
  baseline: number
  // Strict subset of `baseline`, the "Pre-order momentum" subtrahend. `undefined` = not answered
  // yet, NOT zero: a 0 subtrahend double-counts the band preorderUplift already adds.
  baselineRealisedPreorders: number | undefined
  // manual + on-top overlays.
  forecast: number
  // The editable Manual base: baseline + setup + override, ALL on-top overlays stripped.
  manualForecast: number
  // The model stage (no override, adjustments or insights): the Setup column's base.
  model: number
  manualOverride: number | undefined
  // Baseline + setup WITHOUT the override, so a pin can't mask it; what an unedited Manual cell
  // shows.
  setupForecast: number
  // Setup units a manual pin claimed (only its own share), so they never reached Total demand.
  setupSpent: number
  /** Exact per-row setup units, by adjustment id / `insight:<key>`; empty until answered. */
  setupAttribution: Record<string, number>
  /** See `VariantTotals.onTopAttribution`. */
  onTopAttribution: Record<string, number>
  // Setup spent IN FULL, the only case that mutes the number; sub-unit differences don't count.
  setupReplaced: boolean
  // setupForecast − model, measured before the pin so it can't mask it; twin of `onTopDelta`.
  setupDelta: number
  // Overlay adjustments (sales) + committed pre-order uplift, before publish leaves anything out.
  onTopDelta: number
  preorders: number
  /** See `VariantTotals.expectedToCome`. */
  expectedToCome: number | null
  /** Where's Confirmed (`VariantTotals.confirmed`): what every below-book check compares against. */
  confirmed: number
  totalDemand: number
  // totalDemand in weeks past the active publish's coverage (0 without one); the vs-Published
  // overlay subtracts it, since those weeks aren't a change.
  publishTail: number
  /** Why publish leaves this variant out, so its demand reads 0. */
  publishSkip: PublishSkipReason | null
  adjustmentsCount: number
  // No last-52w sales leaf and not NPI, so every number above is a last-resort fallback.
  noRecentBaseline: boolean
  /** Why `noRecentBaseline` holds (see `VariantTotals`). */
  noBasisCause: NoBasisCause | null
  /** The comparable COLLECTION's average produced the baseline (see VariantTotals). */
  baselineSplit: BaselineSplit | null
  // The engine's answer, never `Sku.isNpi`, which lags the forecast tables.
  isNpi: boolean
  /** NPI only: the start of its first 52 weeks, which `npiStage` turns into the lifecycle state. */
  activatedFrom: Date | null
  /** NOOS: the active weeks and source window behind `baseline` (NOOS_PLAN_PERIOD.md). */
  planPeriod: PlanPeriod | null
}

export type ServerTotals = RouterOutputs["demand2"]["getDemandTotals"][number]

export type EffectiveCtx = {
  // Accessor, not a Map: collection-wide rows merge in on lookup (see useDraftAdjMap).
  draftAdjByVariant: { get: (variantId: string) => DraftAdjustment[] | undefined }
  draftForecasts: Record<string, DraftForecast>
  totalsByVariant: Map<string, ServerTotals>
}

// A READER of `getDemandTotals`, not a second implementation: every demand figure is copied off the
// server row, and unsaved edits move them by patching that cache (`patchDemandTotals`), never by
// recomposing here. The legacy Collection table's "Total demand" is a different number.
export const computeEffectiveDemand = (v: IVariantSummary, ctx: EffectiveCtx): EffectiveDemand => {
  const id = v.variantId
  const server = ctx.totalsByVariant.get(id)
  const manual = ctx.draftForecasts[id]
  const shared = {
    manualOverride: manual && manual.source !== "baseline" ? manual.total : undefined,
    adjustmentsCount: (ctx.draftAdjByVariant.get(id) ?? []).length,
  }
  if (!server) return pendingDemand(v, shared)

  const {
    // What publish freezes, so Baseline equals the forecast at rest.
    baseline,
    model,
    manualForecast,
    setupForecast,
    forecast,
    setupSpent,
    totalDemand,
  } = server
  return {
    ...shared,
    setupSpent,
    setupAttribution: server.setupAttribution,
    onTopAttribution: server.onTopAttribution,
    // Sign-safe: a negative setup spends negative units, so compare against the delta.
    setupReplaced: Math.abs(setupSpent) > 0.5 && Math.abs(setupForecast - model - setupSpent) < 0.5,
    baseline,
    baselineRealisedPreorders: server.baselineRealisedPreorders,
    forecast,
    manualForecast,
    model,
    setupForecast,
    setupDelta: setupForecast - model,
    onTopDelta: forecast + server.preorderUplift - manualForecast,
    preorders: server.preorders,
    expectedToCome: server.expectedToCome,
    confirmed: server.confirmed,
    totalDemand,
    publishTail: server.publishTail,
    publishSkip: server.publishSkip,
    noRecentBaseline: server.noRecentBaseline,
    noBasisCause: server.noBasisCause,
    baselineSplit: server.baselineSplit,
    isNpi: server.isNpi,
    activatedFrom: server.activatedFrom,
    planPeriod: server.planPeriod,
  }
}

/**
 * Before the reconstruction answers: all 0 (`TotalView` holds a `LoadingOverlay`, so it isn't
 * rendered), except `baselineRealisedPreorders`, which stays `undefined` (see `EffectiveDemand`).
 */
const pendingDemand = (
  v: IVariantSummary,
  shared: Pick<EffectiveDemand, "manualOverride" | "adjustmentsCount">,
): EffectiveDemand => ({
  ...shared,
  baseline: 0,
  baselineRealisedPreorders: undefined,
  model: 0,
  manualForecast: 0,
  setupForecast: 0,
  forecast: 0,
  totalDemand: 0,
  setupDelta: 0,
  onTopDelta: 0,
  preorders: v.preOrderQuantity ?? 0,
  expectedToCome: null,
  confirmed: v.preOrderQuantity ?? 0,
  setupSpent: 0,
  setupReplaced: false,
  setupAttribution: {},
  onTopAttribution: {},
  publishTail: 0,
  publishSkip: null,
  noRecentBaseline: false,
  noBasisCause: null,
  baselineSplit: null,
  isNpi: false,
  activatedFrom: null,
  planPeriod: null,
})

export const displayedDemandChain = (
  e: Pick<
    EffectiveDemand,
    "baseline" | "setupForecast" | "manualForecast" | "onTopDelta" | "totalDemand"
  >,
) => {
  const baseline = Math.round(e.baseline)
  const setup = Math.round(e.setupForecast)
  const manual = Math.round(e.manualForecast)
  const total = Math.round(e.totalDemand)
  // Either sign: skips take units out, floored negative weeks add them. Within snap it is rounding.
  const notPublished =
    snapDelta(e.totalDemand - e.manualForecast - e.onTopDelta) === 0
      ? 0
      : total - Math.round(e.manualForecast + e.onTopDelta)
  return {
    baseline,
    signals: setup - baseline,
    signalBased: setup,
    manual: manual - setup,
    campaigns: total - notPublished - manual,
    notPublished,
    total,
  }
}
